import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { groupBy, isEmpty, map, sum } from "lodash";
import ActivityHeader from "./activity/ActivityHeader";
import ActivityObject from "./activity/ActivityObject";
import FileAttachments from "./activity/object/attachments/FileAttachments";
import Mentions from "./activity/object/attachments/Mentions";
import LinkAttachments from "./activity/object/attachments/LinkAttachments";
import TagAttachments from "./attachments/TagAttachments";
import ImageAttachments from "./activity/object/attachments/ImageAttachments";
import Interactions from "./activity/object/Interactions";
import store from "../../store";
import { Provider } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import ActivityMeta from "./activity/ActivityMeta";
import { useMutation } from "react-query";
import { fetchApi } from "../../helpers/reactQueryApi";
import SharedActivity from "./activity/object/attachments/SharedActivity";
import ClosedStickyActivity from "./ClosedStickyActivity";
import {
  Activity as ActivityType,
  BaseSurveyObject,
  FileAttachment,
  ImageAttachment,
  LinkAttachment,
  ObjectWithContent,
  PeopleAttachment,
} from "../../@types/activities";
import { useActivityViewCount } from "../../api/activitystreams";
import ErrorInteractions from "./activity/ErrorInteractions.tsx";
import queryClient from "../../helpers/queryClient.tsx";
import useActivitystreamsFromSlugs from "../shared/hooks/useActivitystreamsFromSlugs.ts";
import { useCanWriteToAnyStream } from "../../hooks/useCanWriteToAnyStream.ts";
import Replies from "@/components/activitystreams/activity/Replies.tsx";
import { useBottomSheet } from "@/components/shared/hooks/mobile/useBottomSheet.tsx";
import RepliesWrapper from "@/components/activitystreams/activity/RepliesWrapper.tsx";

type ActivityArgs = {
  activity: ActivityType;
  streamUrl?: string;
  renderedAsPinnedDuplicate?: boolean;
  error?: boolean;
  isNew?: boolean;
  shareActivity?: () => void;
  setStoredActivities?: (activities: ActivityType[]) => void;
  readOnly?: boolean;
  external?: boolean;
};

export default function Activity({
  activity,
  renderedAsPinnedDuplicate = false,
  isNew = false,
  error = false,
  shareActivity = () => {},
  setStoredActivities,
  streamUrl,
  readOnly = false,
  external = false,
}: ActivityArgs) {
  // Externally-served activities (public / member-less view) render read-only
  // even inside the SPA — e.g. a logged-in non-member or network admin viewing
  // an externally-visible stream. The island passes readOnly directly.
  const readOnlyView =
    readOnly || Boolean((activity as { external?: boolean }).external);

  // Public/guest view (externally-served, member-less stream): hide ALL
  // interaction affordances — likes, comments AND the context menu that hosts
  // "Teilen". Deliberately NOT tied to `readOnly`: internal read-only listings
  // like the member-profile "Beiträge" page render the object read-only but must
  // keep sharing working (#4888 / #142 — a readOnly-based gate hid Teilen there).
  const isPublicView =
    external || Boolean((activity as { external?: boolean }).external);
  const activityRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [delayedVisible, setDelayedVisible] = useState(false);

  // IntersectionObserver for viewport check
  useEffect(() => {
    const element = activityRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting);
    });
    observer.observe(element);
    return () => observer.unobserve(element);
  }, []);

  // Only enable polling after the activity has been visible for 5 seconds
  useEffect(() => {
    if (!isVisible) {
      setDelayedVisible(false);
      return;
    }
    const timer = setTimeout(() => setDelayedVisible(true), 5000);
    return () => clearTimeout(timer);
  }, [isVisible]);

  const { data: viewCountData } = useActivityViewCount(
    activity.id,
    activity.view_count != null && delayedVisible,
  );

  const [closed, setClosed] = useState(renderedAsPinnedDuplicate);
  const [stickyConfirmed, setStickyConfirmed] = useState(false);

  const [translatedContent, setTranslatedContent] = useState<{
    language?: string;
    content?: string;
    name?: string;
    description?: string;
  }>({
    language: undefined,
    content: undefined,
    name: undefined,
    description: undefined,
  });

  const [translatedSharedContent, setTranslatedSharedContent] = useState<{
    language?: string;
    content?: string;
    name?: string;
    description?: string;
  }>({
    language: undefined,
    content: undefined,
    name: undefined,
    description: undefined,
  });

  // Read-only (external, logged-out) views can't track reads — never show
  // these as unread (matches the old server-rendered external view).
  const [isUnread, setIsUnread] = useState(
    readOnlyView ? false : activity.unread,
  );
  const hasTrackedView = useRef(false);
  const markAsReadMarker = useRef<HTMLDivElement>(null);

  const { toggleSheet, ...sheetMehtods } = useBottomSheet();

  const { mutate: confirmSticky } = useMutation(
    () =>
      fetchApi(`/activities/${activity.id}/confirm_sticky`, {
        method: "POST",
      }),
    { onSuccess: () => setStickyConfirmed(true) },
  );

  const { mutate: markAsRead } = useMutation(
    () =>
      fetchApi(`/activities/${activity.id}/mark_as_read`, {
        method: "POST",
      }),
    {
      onMutate: () => setIsUnread(false),
      onSuccess: () => queryClient.invalidateQueries(["unreads"]),
      onError: () => setIsUnread(true),
    },
  );

  const markAsReadRef = useRef(markAsRead);
  markAsReadRef.current = markAsRead;

  useEffect(() => {
    // No read-tracking without a member: the mark-as-read POST would 401 and
    // its onError would flip the activity back to "unread".
    if (readOnlyView) return;
    if (hasTrackedView.current) return;
    if (!activity.unread && activity.viewed) {
      hasTrackedView.current = true;
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        hasTrackedView.current = true;
        markAsReadRef.current();
      }
    });

    const element = markAsReadMarker.current;
    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  const canWriteToAnyStream = useCanWriteToAnyStream();

  const groupedAttachments = groupBy(activity.object.attachments, "type");

  const fileAttachments = groupedAttachments.file;
  const mentions = groupedAttachments.person;
  const linkAttachaments = groupedAttachments.link;

  const imageAttachments = groupedAttachments.image;

  const { like_count, like_list_short, like_list_long } = activity.object;

  const tags = activity.object.tags;

  const eventFiles =
    activity.object.type === "appointment"
      ? activity.object.event_files
      : undefined;

  const hasAttachments =
    !isEmpty(fileAttachments) ||
    !isEmpty(mentions) ||
    !isEmpty(linkAttachaments) ||
    !isEmpty(imageAttachments) ||
    !isEmpty(tags) ||
    !isEmpty(eventFiles);

  function totalAttachmentSize() {
    const fileSizes = map(fileAttachments, "size");
    const imageSizes = map(imageAttachments, "size");
    const eventFileSizes = map(eventFiles, "size");

    return sum([...fileSizes, ...imageSizes, ...eventFileSizes]);
  }

  const streamsFromActivity = useActivitystreamsFromSlugs({
    slugs: activity.activitystream_slugs,
    includeSubscriberCount: true,
  });

  const canShare =
    activity.object.shareable &&
    isEmpty(activity.shared_from) &&
    activity.can.share &&
    canWriteToAnyStream;

  function translateObject(data: any, language: string) {
    if (activity.object.type === "appointment") {
      setTranslatedContent({
        language,
        name: data.name,
        description: data.text,
      });
    } else {
      setTranslatedContent({
        language,
        content: data.text,
      });
    }

    if (
      activity.shared_from &&
      data.shared_text &&
      setTranslatedSharedContent
    ) {
      setTranslatedSharedContent({
        language,
        content: data.shared_text,
      });
    }
  }

  function resetTranslation(language: string) {
    setTranslatedContent({ language });

    if (activity.shared_from && setTranslatedSharedContent) {
      setTranslatedSharedContent({ language });
    }
  }

  if (renderedAsPinnedDuplicate && closed)
    return (
      <ClosedStickyActivity
        stickyType={activity.sticky}
        setClosed={setClosed}
        renderActivityMeta={() => (
          <ActivityMeta
            activityId={activity.id}
            published={activity.published}
            isNew={isNew}
            actorUpdatedAt={activity.actor_updated_at}
            actorUpdatedBy={activity.actor_updated_by}
            archivedAt={activity.archived_at}
            archivedByName={activity.archived_by_name}
            contentUpdatedAt={
              (activity.object as ObjectWithContent).content_updated_at
            }
            contentUpdatedBy={
              (activity.object as ObjectWithContent).content_updated_by
            }
            streamsFromActivity={streamsFromActivity}
            foreignActivityStreams={activity.foreign_activitystreams}
          />
        )}
        content={
          activity.object.type === "appointment"
            ? undefined
            : (activity.object as ObjectWithContent).content
        }
        appointment={
          activity.object.type === "appointment"
            ? {
                name: translatedContent.name || activity.object.name,
                date: activity.object.date,
              }
            : undefined
        }
      />
    );

  const objectArgs =
    activity.object.type === "appointment"
      ? {
          description:
            translatedContent.description || activity.object.description,
          name: translatedContent.name || activity.object.name,
        }
      : activity.object.type === "article"
        ? {}
        : { content: translatedContent.content || activity.object.content };

  return (
    <Provider store={store}>
      <div
        ref={activityRef}
        id={`activity-${activity.id}`}
        className={classNames("activity", {
          "border-primary/25! bg-primary/10!": renderedAsPinnedDuplicate,
          "activity-error": error,
          "activity--unread": isUnread,
        })}
      >
        <ActivityHeader
          activityId={activity.id}
          actor={activity.actor}
          external={isPublicView}
          isNew={isNew}
          renderedAsPinnedDuplicate={renderedAsPinnedDuplicate}
          sticky={activity.sticky}
          error={error}
          closeActivity={(e) => {
            e.preventDefault();
            setClosed(true);
          }}
          published={activity.published}
          actorUpdatedAt={activity.actor_updated_at}
          actorUpdatedBy={activity.actor_updated_by}
          archivedAt={activity.archived_at}
          archivedByName={activity.archived_by_name}
          activityObject={activity.object}
          translatedContent={translatedContent}
          foreignActivityStreams={activity.foreign_activitystreams}
          emailRecommendationCount={{
            internal: activity.internal_email_recommendation_count,
            external: activity.external_email_recommendation_count,
          }}
          emailRecommendationInfo={{
            hasAttachments,
            totalAttachmentSize: totalAttachmentSize(),
            lastRecommendation: activity.email_recommendation,
          }}
          teaser={activity.teaser}
          emailRecommendedTo={activity.email_recommended_to}
          emailRecommendedToMembers={activity.email_recommended_to_members}
          externalEmailRecommendedTo={activity.external_email_recommended_to}
          streamsFromActivity={streamsFromActivity}
          can={activity.can}
          repliesClosed={activity.replies_closed}
          hidden={activity.hidden}
          plannedForNewsletterAt={activity.planned_for_newsletter_at}
          iSubscribedEmail={activity.i_subscribed_email}
          canShare={canShare}
          shareActivity={shareActivity}
          streamUrl={streamUrl}
          sentInNewsLetterAt={activity.sent_in_newsletter_at}
          type={activity.object.type}
          translateObject={translateObject}
          resetTranslation={resetTranslation}
        />
        <div className="activity-body" data-testid="activity-object">
          <ActivityObject
            activityId={activity.id}
            {...activity.object}
            {...objectArgs}
            translated={!!translatedContent.language}
            readOnly={readOnlyView}
          />
          <div ref={markAsReadMarker} className="h-1" />
          <div className="activity-attachments mt-6 flex flex-col gap-2 divide-y divide-dashed divide-neutral *:pt-3">
            {!isEmpty(imageAttachments) && (
              <ImageAttachments
                images={imageAttachments as ImageAttachment[]}
              />
            )}
            {!isEmpty(mentions) && (
              <Mentions mentions={mentions as PeopleAttachment[]} />
            )}
            {!isEmpty(linkAttachaments) && (
              <LinkAttachments links={linkAttachaments as LinkAttachment[]} />
            )}
            {!isEmpty(fileAttachments) && (
              <FileAttachments files={fileAttachments as FileAttachment[]} />
            )}
            {tags && !isEmpty(tags) && <TagAttachments tags={tags} />}
            {!isEmpty(activity.shared_from) && (
              <SharedActivity
                {...activity.shared_from}
                translatedContent={translatedSharedContent}
                readOnly={readOnlyView}
              />
            )}
            {!error && !isPublicView && (
              <Interactions
                activityId={activity.id}
                likeListShort={like_list_short}
                likeListLong={like_list_long}
                likeCount={like_count}
                iLiked={!isEmpty(activity.object.my_like) || false}
                canLike={activity.object.can?.like}
                reactions={activity.object.reactions}
                myReaction={activity.object.my_reaction}
                canReact={activity.object.can?.react}
                hasAttachments={hasAttachments}
                shareable={canShare}
                likes={activity.object.likes}
                shareActivity={shareActivity}
                closed={(activity.object as BaseSurveyObject).closed}
                type={activity.object.type}
                translateObject={translateObject}
                resetTranslation={resetTranslation}
                translatedContent={translatedContent}
                error={error}
                emailRecommendationCount={{
                  internal: activity.internal_email_recommendation_count,
                  external: activity.external_email_recommendation_count,
                }}
                emailRecommendationInfo={{
                  hasAttachments,
                  totalAttachmentSize: totalAttachmentSize(),
                  lastRecommendation: activity.email_recommendation,
                }}
                teaser={activity.teaser}
                streamsFromActivity={streamsFromActivity}
                can={activity.can}
                repliesClosed={activity.replies_closed}
                hidden={activity.hidden}
                plannedForNewsletterAt={activity.planned_for_newsletter_at}
                iSubscribedEmail={activity.i_subscribed_email}
                activityObject={activity.object}
                actor={activity.actor}
                repliesCount={activity.object.reply_count}
                sticky={activity.sticky}
                archivedAt={activity.archived_at}
                streamUrl={streamUrl}
                toggleReplies={toggleSheet}
                readOnly={readOnlyView}
                viewCount={viewCountData?.view_count ?? activity.view_count}
                emailDeliveryCount={
                  viewCountData?.email_delivery_count ??
                  activity.email_delivery_count
                }
                reachCount={viewCountData?.reach_count ?? activity.reach_count}
              />
            )}
            {!readOnlyView && (
              <RepliesWrapper {...sheetMehtods}>
                <Replies
                  activityId={activity.id}
                  replies={activity.object.replies}
                  repliesCount={activity.object.reply_count}
                  repliesClosed={activity.replies_closed}
                  canReply={activity.object.can.reply}
                  showAllInitially={window.isApp}
                  type="replies"
                />
              </RepliesWrapper>
            )}
            {renderedAsPinnedDuplicate &&
              activity.sticky === "confirmable" &&
              !stickyConfirmed && (
                <div className="flex justify-center items-center">
                  <FontAwesomeIcon icon={faCheck} />
                  <button
                    role="button"
                    className="btn btn-sm btn-link no-underline hover:underline text-muted"
                    onClick={(e) => {
                      e.preventDefault();
                      confirmSticky();
                    }}
                  >
                    {I18n.t("activitystreams.sticky.confirm")}
                  </button>
                </div>
              )}
            {error && setStoredActivities && (
              <ErrorInteractions
                activity={activity}
                setStoredActivities={setStoredActivities}
              />
            )}
          </div>
        </div>
      </div>
    </Provider>
  );
}
